import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { EmptyState } from '../../components/common/EmptyState';
import { Panel } from '../../components/common/Panel';
import { CardSkeleton } from '../../components/common/Skeleton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

export const Route = createFileRoute('/open-banking/')({
  component: OpenBankingPage,
});

function OpenBankingPage() {
  usePageTitle('Bankkopplingar');

  const sessionsQuery = trpc.enableBanking.sessions.useQuery();
  const [showConnect, setShowConnect] = useState(false);

  if (sessionsQuery.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <CardSkeleton lines={2} />
        <CardSkeleton lines={3} />
      </div>
    );
  }

  const sessions = sessionsQuery.data ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bankkopplingar</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Banker som Budgie hämtar transaktioner från via Enable Banking.
          </p>
        </div>
        <Button
          variant={showConnect ? 'outline' : 'default'}
          onClick={() => setShowConnect((v) => !v)}
        >
          {showConnect ? 'Avbryt' : 'Anslut bank'}
        </Button>
      </header>

      {showConnect && (
        <ConnectBankCard
          onSuccess={() => {
            setShowConnect(false);
          }}
        />
      )}

      {sessions.length === 0 && !showConnect && (
        <EmptyState
          icon="🏦"
          title="Inga banker anslutna"
          description="Anslut din bank för att hämta transaktioner automatiskt via Enable Banking."
          action={{ label: 'Anslut bank', onClick: () => setShowConnect(true) }}
        />
      )}

      {sessions.map((session) => (
        <SessionCard key={session.id} session={session} />
      ))}

      <ConnectLinksCard />
    </div>
  );
}

// ── Connect Links Card ─────────────────────────────────────────────────────

/** Shareable links that let a household member reconnect their bank without logging in */
function ConnectLinksCard() {
  const [country] = useState('SE');
  const [label, setLabel] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [newLink, setNewLink] = useState('');

  const utils = trpc.useUtils();
  const linksQuery = trpc.enableBanking.connectLinks.useQuery();
  const banksQuery = trpc.enableBanking.listBanks.useQuery({ country });
  const create = trpc.enableBanking.createConnectLink.useMutation({
    onSuccess: (data) => {
      setNewLink(`${window.location.origin}/connect/${data.token}`);
      setLabel('');
      void utils.enableBanking.connectLinks.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });
  const revoke = trpc.enableBanking.revokeConnectLink.useMutation({
    onSuccess: () => {
      toast.success('Länken har återkallats');
      void utils.enableBanking.connectLinks.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const links = linksQuery.data ?? [];

  return (
    <Panel
      title="Delningslänkar"
      description="Skicka en länk till en annan person i hushållet så kan hen förnya sin bankkoppling med BankID utan att logga in i Budgie. Länken kan återanvändas tills du återkallar den."
    >
      <div className="space-y-4">
        {links.length > 0 && (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {links.map((link) => (
              <li
                key={link.id}
                className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm"
              >
                <div className="min-w-0">
                  <span className="font-medium">{link.label}</span>
                  <span className="ml-2 text-muted-foreground">{link.aspspName}</span>
                  <div className="text-xs text-muted-foreground">
                    {link.lastUsedAt
                      ? `Senast använd ${new Date(link.lastUsedAt).toLocaleDateString('sv-SE')}`
                      : 'Aldrig använd'}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-negative-soft hover:text-destructive"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate({ id: link.id })}
                >
                  Återkalla
                </Button>
              </li>
            ))}
          </ul>
        )}

        {newLink && (
          <div className="space-y-2 rounded-xl bg-positive-soft p-3">
            <Label className="text-positive">
              Ny länk — kopiera den nu, den visas bara en gång
            </Label>
            <div className="flex gap-2">
              <Input
                readOnly
                value={newLink}
                className="bg-card text-xs"
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(newLink);
                  toast.success('Länk kopierad');
                }}
              >
                Kopiera
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div className="space-y-2">
            <Label htmlFor="connect-link-label">Namn</Label>
            <Input
              id="connect-link-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="t.ex. Sara"
              maxLength={60}
            />
          </div>
          <div className="space-y-2">
            <Label>Bank</Label>
            <Select value={selectedBank} onValueChange={setSelectedBank}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={banksQuery.isLoading ? 'Laddar banker…' : 'Välj bank…'} />
              </SelectTrigger>
              <SelectContent>
                {(banksQuery.data ?? []).map((bank) => (
                  <SelectItem key={bank.name} value={bank.name}>
                    {bank.name} {bank.beta && '(beta)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            disabled={!label.trim() || !selectedBank || create.isPending}
            onClick={() =>
              create.mutate({ label: label.trim(), aspspName: selectedBank, aspspCountry: country })
            }
          >
            {create.isPending ? 'Skapar…' : 'Skapa länk'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

// ── Connect Bank Card ──────────────────────────────────────────────────────

function ConnectBankCard({ onSuccess }: { onSuccess: () => void }) {
  const [country] = useState('SE');
  const [selectedBank, setSelectedBank] = useState('');

  const banksQuery = trpc.enableBanking.listBanks.useQuery({ country });
  const startAuth = trpc.enableBanking.startAuth.useMutation({
    onSuccess: (data) => {
      onSuccess();
      window.location.href = data.url;
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const banks = banksQuery.data ?? [];

  return (
    <Panel title="Anslut ny bank" description="Välj din bank och godkänn med BankID.">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-2">
          <Label>Bank</Label>
          {banksQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Laddar banker…</p>
          ) : (
            <Select value={selectedBank} onValueChange={setSelectedBank}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Välj bank…" />
              </SelectTrigger>
              <SelectContent>
                {banks.map((bank) => (
                  <SelectItem key={bank.name} value={bank.name}>
                    {bank.name} {bank.beta && '(beta)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Button
          onClick={() =>
            startAuth.mutate({
              aspspName: selectedBank,
              aspspCountry: country,
            })
          }
          disabled={!selectedBank || startAuth.isPending}
        >
          {startAuth.isPending ? 'Ansluter…' : 'Anslut via BankID'}
        </Button>
      </div>
    </Panel>
  );
}

// ── Session Card ───────────────────────────────────────────────────────────

type SessionData = {
  id: string;
  aspspName: string;
  aspspCountry: string;
  validUntil: string | Date | null;
  accounts: AccountData[] | null;
  createdAt: string | Date | null;
};

type AccountData = {
  uid?: string | undefined;
  // biome-ignore lint/style/useNamingConvention: mirrors the Enable Banking API shape
  account_id?: { iban?: string | undefined } | undefined;
  name?: string | undefined;
  currency?: string | undefined;
};

function SessionCard({ session }: { session: SessionData }) {
  const validUntil = session.validUntil ? new Date(session.validUntil) : null;
  const isExpired = validUntil ? validUntil < new Date() : false;

  return (
    <Panel
      title={session.aspspName}
      description={
        validUntil ? `Giltig t.o.m. ${validUntil.toLocaleDateString('sv-SE')}` : undefined
      }
      action={
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            isExpired ? 'bg-negative-soft text-negative' : 'bg-positive-soft text-positive',
          )}
        >
          {isExpired ? 'Utgången' : 'Aktiv'}
        </span>
      }
    >
      <div className="space-y-3">
        {session.accounts?.map((account: AccountData) => (
          <AccountRow
            key={account.uid}
            sessionId={session.id}
            account={account}
            isExpired={isExpired}
          />
        ))}
      </div>
    </Panel>
  );
}

// ── Account Row ────────────────────────────────────────────────────────────

function AccountRow({
  sessionId,
  account,
  isExpired,
}: {
  sessionId: string;
  account: AccountData;
  isExpired: boolean;
}) {
  const utils = trpc.useUtils();
  const [showFetch, setShowFetch] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const balancesQuery = trpc.enableBanking.balances.useQuery(
    { sessionId, accountUid: account.uid! },
    { enabled: !isExpired && !!account.uid },
  );

  const fetchTx = trpc.enableBanking.fetchTransactions.useMutation({
    onSuccess: (data) => {
      if (data.totalCount === 0) {
        toast.info('Inga nya transaktioner hittades.');
      } else {
        toast.success(
          `${data.totalCount} transaktioner hämtade (${data.duplicateCount} dubbletter). Granska i Importera-fliken.`,
        );
        void utils.transaction.pendingImport.invalidate();
      }
      setShowFetch(false);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const iban = account.account_id?.iban;
  const balances = balancesQuery.data;
  const balance = balances && balances.length > 0 ? balances[0] : undefined;

  return (
    <div className="space-y-3 rounded-xl bg-muted/60 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">{account.name ?? 'Konto'}</p>
          {iban && <p className="truncate text-xs text-muted-foreground tabular-nums">{iban}</p>}
          {balance && (
            <p className="mt-0.5 text-sm text-muted-foreground tabular-nums">
              Saldo:{' '}
              <span className="font-semibold text-foreground">
                {Number(balance.amount).toLocaleString('sv-SE', { minimumFractionDigits: 2 })}{' '}
                {balance.currency}
              </span>
            </p>
          )}
          {balancesQuery.isLoading && (
            <p className="mt-0.5 text-xs text-muted-foreground">Laddar saldo…</p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFetch((v) => !v)}
          disabled={isExpired}
        >
          {showFetch ? 'Avbryt' : 'Hämta transaktioner'}
        </Button>
      </div>

      {showFetch && (
        <div className="space-y-3 border-t border-border pt-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Från datum (valfritt)</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-card"
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Till datum (valfritt)</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-card"
              />
            </div>
          </div>
          <Button
            onClick={() =>
              fetchTx.mutate({
                sessionId,
                accountUid: account.uid!,
                ...(dateFrom ? { dateFrom } : {}),
                ...(dateTo ? { dateTo } : {}),
              })
            }
            disabled={fetchTx.isPending}
          >
            {fetchTx.isPending ? 'Hämtar transaktioner…' : 'Hämta'}
          </Button>
          {fetchTx.error && <p className="text-sm text-negative">{fetchTx.error.message}</p>}
        </div>
      )}
    </div>
  );
}
